import { Activity } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useMyAvailability } from './hooks'
import { TeamAvailabilityRow } from './TeamAvailabilityRow'
import type { AvailabilityStatus } from './types'

const STATUS_DOT: Record<AvailabilityStatus, string> = {
  READY: 'bg-emerald-500',
  BUSY: 'bg-amber-500',
  AWAY: 'bg-slate-400',
}

/**
 * Topbar widget showing per-team availability status. Visible only when the
 * user belongs to at least one team with enables_availability=true (server
 * filters; empty array hides the widget entirely).
 *
 * <p>Click opens a popover listing each team with a status selector.</p>
 */
export function MyAvailabilityWidget() {
  const { data: availability = [], isLoading } = useMyAvailability()

  if (isLoading || availability.length === 0) return null

  // Aggregate dot color: prefer READY > BUSY > AWAY for the topbar indicator
  const aggregate: AvailabilityStatus = availability.some(a => a.status === 'READY')
    ? 'READY'
    : availability.some(a => a.status === 'BUSY')
      ? 'BUSY'
      : 'AWAY'

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Dostępność">
              <span className="relative inline-flex items-center justify-center">
                <Activity className="size-4" />
                <span
                  className={`absolute -bottom-0.5 -right-0.5 size-2 rounded-full ring-2 ring-background ${STATUS_DOT[aggregate]}`}
                />
              </span>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Dostępność</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="border-b px-3 py-2">
          <div className="text-sm font-medium">Twoja dostępność</div>
          <div className="text-xs text-slate-500">
            Ustaw status per zespół do auto-przypisywania spraw.
          </div>
        </div>
        <ul className="divide-y">
          {availability.map(av => (
            <TeamAvailabilityRow key={av.teamId} availability={av} />
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
